import { Injectable } from '@angular/core';
import { CarSearchRequest, FlightSearchRequest, HotelSearchRequest, SearchType } from '../../../shared/models/search-bar';
import { BehaviorSubject, catchError, map, of } from 'rxjs';
import { DataService } from '../../../shared/data/data.service';

@Injectable({providedIn: 'root'})
export class SearchBarService {

  public searchType = new BehaviorSubject<SearchType>(SearchType.FLIGHT);
  private _flightSearchRequest = new BehaviorSubject<FlightSearchRequest>({} as FlightSearchRequest);
  public readonly flightSearchRequest$ = this._flightSearchRequest.asObservable();

  private _flightSearchData = new BehaviorSubject<any>({});
  public readonly flightSearchData$ = this._flightSearchData.asObservable();

  private _hotelSearchRequest = new BehaviorSubject<HotelSearchRequest>(this.hotelSearchRequest);
  public readonly hotelSearchRequest$ = this._hotelSearchRequest.asObservable();

  private _hotelSearchData = new BehaviorSubject<any>({});
  public readonly hotelSearchData$ = this._hotelSearchData.asObservable();

  private _carSearchRequest = new BehaviorSubject<CarSearchRequest>(this.carSearchRequest);
  public readonly carSearchRequest$ = this._carSearchRequest.asObservable();

  private _carSearchData = new BehaviorSubject<any>({});
  public readonly carSearchData$ = this._carSearchData.asObservable();

  private _errorString = new BehaviorSubject<string>('');
  public readonly errorString$ = this._errorString.asObservable();

  private readonly _isLoading = new BehaviorSubject<boolean>(false);
  public readonly isLoading$ = this._isLoading.asObservable();

  constructor(
    private readonly dataService: DataService,
  ) { }

  public changeFlightSearchRequest(req: FlightSearchRequest) {
    if(req?.journeyType){
    localStorage.setItem('flightSearchRequest', JSON.stringify(req));
    this._flightSearchRequest.next(req);
    }
  }

  get flightSearchRequest(): FlightSearchRequest {
    const storedRequest = localStorage.getItem('flightSearchRequest');
    if (storedRequest) {
      return JSON.parse(storedRequest) as FlightSearchRequest;
    }
    return {} as FlightSearchRequest;
  }

  public resetFlightSearchRequest() {
    this._flightSearchRequest.next({} as FlightSearchRequest);
  }

  public changeHotelSearchRequest(req: HotelSearchRequest) {
    if(req?.checkout){
    localStorage.setItem('hotelSearchRequest', JSON.stringify(req));
    this._hotelSearchRequest.next(req);
    }
  }

  public resetCarSearchRequest() {
    this._carSearchRequest.next({} as CarSearchRequest);
  }

  public changeCarSearchRequest(req: CarSearchRequest) {
    if(req?.pickup_id){
    localStorage.setItem('carSearchRequest', JSON.stringify(req));
    this._carSearchRequest.next(req);
    }
  }

  get hotelSearchRequest(): HotelSearchRequest {
    const storedRequest = localStorage.getItem('hotelSearchRequest');
    if (storedRequest) {
      return JSON.parse(storedRequest) as HotelSearchRequest;
    }
    return {} as HotelSearchRequest;
  }

  get carSearchRequest(): CarSearchRequest {
    const storedRequest = localStorage.getItem('carSearchRequest');
    if (storedRequest) {
      return JSON.parse(storedRequest) as CarSearchRequest;
    }
    return {} as CarSearchRequest;
  }

  public resetHotelSearchRequest() {
    this._hotelSearchRequest.next({} as HotelSearchRequest);
  }

  public setFlightSearchData(data: any) {
    this._flightSearchData.next(data);
  }

  public getFlightSearchData(): void {
    this._flightSearchData.next({});
    this._isLoading.next(true);
    localStorage.setItem('lastSearchFlightRequest', JSON.stringify(this._flightSearchRequest.value));
    this.dataService.getFlightSearchData(this._flightSearchRequest.value)
    .pipe(
      map(data => {
        this._isLoading.next(false); // Set loading to false after data is fetched
        this._errorString.next(''); // Reset error string before fetching new data
        if(data?.status?.error) {
          this._errorString.next(data.status.error);
          return {};
        }
        return data;
      }),
      catchError((err) => {
        this._isLoading.next(false); // Set loading to false on error
        this._errorString.next('An error occurred while fetching flight data.'); // Set a generic error message
        return of(err);
      })
    )
    .subscribe(data => {
      this.setFlightSearchData(data);
    });
  }

  public getHotelSearchData(): void {
    this._hotelSearchData.next({});
    this._isLoading.next(true);
    this._errorString.next(''); // Reset error string before fetching new data
    localStorage.setItem('lastSearchHotelRequest', JSON.stringify(this._hotelSearchRequest.value));
    this.dataService.getHotelSearchData(this._hotelSearchRequest.value).pipe(
      map(data => {
        this._isLoading.next(false); // Set loading to false after data is fetched
        if(data?.status?.error) {
          this._errorString.next(data.status.error);
          return {
            message: data.status.error
          };
        }
        return data
      }),
      catchError((err) => {
        this._isLoading.next(false); // Set loading to false on error
        return of(err)
      })
    ).subscribe(data => {
      this._hotelSearchData.next(data);
    });
  }

  public getCarSearchData(): void {
    this._carSearchData.next({});
    this._isLoading.next(true);
    this._errorString.next(''); // Reset error string before fetching new data
    this.dataService.getCarSearchData(this._carSearchRequest.value).pipe(
      map(data => {
        this._isLoading.next(false); // Set loading to false after data is fetched
        if(data?.status?.error) {
          this._errorString.next(data.status.error);
          return {
            message: data.status.error
          };
        }
        return data
      }),
      catchError((err) => {
        this._isLoading.next(false); // Set loading to false on error
        this._errorString.next('An error occurred while fetching car data.'); // Set a generic error message
        return of(err)
      })
    ).subscribe(data => {
      this._carSearchData.next(data);
    });
  }

  getHotelMoreData(payload: { sessionId: any; nextToken: any; }): void {
    this._isLoading.next(true);
    this.dataService.getHotelMoreData(payload).pipe(
      map(data => {
        this._isLoading.next(false); // Set loading to false after data is fetched
        if(data?.status?.error) {
          this._errorString.next(data.status.error);
          return {
            message: data.status.error
          };
        }
        return data
      }),
      catchError((err) => {
        this._isLoading.next(false); // Set loading to false on error
        return of(err)
      })
    ).subscribe(data => {
      let d = this._hotelSearchData.value;
      if (d?.itineraries) {
        d.itineraries = [...d.itineraries, ...data?.itineraries];
      }
      if (data?.status) {
        d.status = {
          ...d.status,
          ...data.status
        }
      }

      console.log('Hotel more data:', d);
      this._hotelSearchData.next(d);
    });
  }

  getHotelSearchDataWithFilter(updatedFormValue: void) {
    this._hotelSearchData.next({});
    this._isLoading.next(true);
    this.dataService.getHotelSearchDataWithFilter(updatedFormValue).pipe(
      map(data => {
        this._isLoading.next(false); // Set loading to false after data is fetched
        this._errorString.next(''); // Reset error string before fetching new data
        if(data?.status?.error) {
          this._errorString.next(data.status.error);
          return {
            message: data.status.error
          };
        }
        return data
      }),
      catchError((err) => {
        this._isLoading.next(false); // Set loading to false on error
        this._errorString.next('An error occurred while fetching hotel data.'); // Set a generic error message
        return of(err)
      })
    ).subscribe(data => {
      this._hotelSearchData.next(data);
    })

  }

  getHotelMoreDataWithFilter(payload: { sessionId: any; nextToken: any; filterKey?: string }): void {
    this._isLoading.next(true);
    this.dataService.getHotelMoreDataWithFilter(payload)
    .pipe(
      map(data => {
        this._isLoading.next(false); // Set loading to false after data is fetched
        if(data?.status?.error) {
          this._errorString.next(data.status.error);
          return {
            message: data.status.error
          };
        }
        return data
      }),
      catchError((err) => {
        this._isLoading.next(false); // Set loading to false on error
        return of(err)
      })
    ).subscribe(data => {
      let d = this._hotelSearchData.value;
      if (d?.itineraries) {
        d.itineraries = [...d.itineraries, ...data?.itineraries];
      }
      if (data?.status) {
        d.status = {
          ...d.status,
          ...data.status
        }
      }

      console.log('Hotel more data:', d);
      this._hotelSearchData.next(d);
    });
  }

  public toQueryString(obj: any, prefix = ''): string {
    const str: string[] = [];
    for (const p in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, p)) {
        const k = prefix ? `${prefix}[${p}]` : p;
        const v = obj[p];
        if (Array.isArray(v)) {
          v.forEach((item, idx) => {
            if (typeof item === 'object' && item !== null) {
              str.push(this.toQueryString(item, `${k}[${idx}]`));
            } else {
              str.push(`${encodeURIComponent(`${k}[${idx}]`)}=${encodeURIComponent(item)}`);
            }
          });
        } else if (v !== null && typeof v === 'object') {
          // If the value is a Date object, serialize as ISO string
          if (v instanceof Date) {
            str.push(`${encodeURIComponent(k)}=${encodeURIComponent(v.toISOString())}`);
          } else {
            str.push(this.toQueryString(v, k));
          }
        } else if (v !== undefined) {
          // Convert Date objects to ISO string
          const value = v instanceof Date ? v.toISOString() : v;
          str.push(`${encodeURIComponent(k)}=${encodeURIComponent(value)}`);
        }
      }
    }
    return str.join('&');
  }

  public fromQueryParams(params: any): any {
    // This function flattens nested query params like occupancy[0][child_age][0]=5
    // and also supports direct array values
    const result: any = {};
    // --- roomAndGuest reconstruction ---
    const roomAndGuest: any = {};
    Object.keys(params).forEach(key => {
      if (key.startsWith('roomAndGuest[')) {
        const match = key.match(/^roomAndGuest\[(\w+)\](?:\[(\d+)\])?$/);
        if (match) {
          const prop = match[1];
          const idx = match[2];
          if (idx !== undefined) {
            // It's an array property
            if (!roomAndGuest[prop]) roomAndGuest[prop] = [];
            roomAndGuest[prop][parseInt(idx, 10)] = isNaN(Number(params[key])) ? params[key] : Number(params[key]);
          } else {
            // It's a simple property
            roomAndGuest[prop] = isNaN(Number(params[key])) ? params[key] : Number(params[key]);
          }
        }
      }
    });
    if (Object.keys(roomAndGuest).length) {
      result.roomAndGuest = roomAndGuest;
    }
    // --- end roomAndGuest reconstruction ---
    for (const key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
      // Skip roomAndGuest keys (already handled)
      if (key.startsWith('roomAndGuest[')) continue;
      // occupancy[0][child_age][0] style
      let match = key.match(/([\w]+)\[(\d+)\]\[([\w]+)\]\[(\d+)\]/);
      if (match) {
        const arrKey = match[1];
        const idx = +match[2];
        const prop = match[3];
        const subIdx = +match[4];
        result[arrKey] = result[arrKey] || [];
        result[arrKey][idx] = result[arrKey][idx] || {};
        result[arrKey][idx][prop] = result[arrKey][idx][prop] || [];
        result[arrKey][idx][prop][subIdx] = params[key];
        continue;
      }
      // occupancy[0][child_age] style (not used in your case, but for completeness)
      match = key.match(/([\w]+)\[(\d+)\]\[([\w]+)\]/);
      if (match) {
        const arrKey = match[1];
        const idx = +match[2];
        const prop = match[3];
        result[arrKey] = result[arrKey] || [];
        result[arrKey][idx] = result[arrKey][idx] || {};
        if (typeof params[key] === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(params[key])) {
          result[arrKey][idx][prop] = new Date(params[key]);
        } else {
          result[arrKey][idx][prop] = params[key];
        }
        continue;
      }
      // occupancy[0] style
      match = key.match(/([\w]+)\[(\d+)\]/);
      if (match) {
        const arrKey = match[1];
        const idx = +match[2];
        result[arrKey] = result[arrKey] || [];
        result[arrKey][idx] = params[key];
        continue;
      }
      // fallback
      if (key.endsWith('[]')) {
        const arrKey = key.replace(/\[\]$/, '');
        try {
          result[arrKey] = JSON.parse(params[key]);
        } catch {
          result[arrKey] = params[key];
        }
      } else {
        // Convert ISO date strings to Date objects if possible
        if (typeof params[key] === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(params[key])) {
          result[key] = new Date(params[key]);
        } else {
          result[key] = params[key];
        }
      }
    }
    return result;
  }

  formatFilterFormValue(value: any) {
    if (!value || !value.filters) return value;
    const filters = { ...value.filters };
    const arrayFields = [
      'rating',
      'tripadvisorRating',
      'hotelName',
      'faretype',
      'propertyType',
      'facility'
    ];
    // Convert arrays to comma-separated strings, ensure all are strings
    arrayFields.forEach(field => {
      const v = filters[field];
      if (Array.isArray(v)) {
        filters[field] = v.length ? v.join(',') : '';
      } else if (typeof v !== 'string') {
        filters[field] = '';
      }
    });
    // Remove blank keys
    Object.keys(filters).forEach(key => {
      if (filters[key] === '' || filters[key] === null || filters[key] === undefined) {
        delete filters[key];
      }
    });
    // Remove price if min and max are 0
    if (
      filters.price &&
      typeof filters.price === 'object' &&
      Number(filters.price.min) === 0 &&
      Number(filters.price.max) === 0
    ) {
      delete filters.price;
    }
    return { ...value, filters };
  }

    /**
   * Distributes adults and children into rooms for occupancy array.
   * Each room gets at least one adult. Remaining adults and children are distributed as evenly as possible.
   * Children ages are distributed in order from the provided array, or defaulted to 1 if not enough ages are provided.
   */
  public generateOccupancy(roomCount: number, adultCount: number, childCount: number, childAges: number[] = []): any[] {
    if (roomCount === 0 || adultCount < roomCount) {
      throw new Error('Each room must have at least one adult.');
    }

    const occupancy = [];
    let remainingAdults = adultCount - roomCount; // after 1 adult per room
    let childAgeIdx = 0;

    for (let i = 1; i <= roomCount; i++) {
      // Each room gets 1 adult by default
      let adultsInRoom = 1;

      // Distribute remaining adults as evenly as possible
      if (remainingAdults > 0) {
        adultsInRoom += 1;
        remainingAdults--;
      }

      // Distribute children as evenly as possible across all rooms
      const baseChildrenPerRoom = Math.floor(childCount / roomCount);
      const extraChildren = childCount % roomCount;
      let childrenInRoom = baseChildrenPerRoom + (i <= extraChildren ? 1 : 0);

      // Assign child ages
      const child_age = [];
      for (let j = 0; j < childrenInRoom; j++) {
        child_age.push(childAges[childAgeIdx] !== undefined ? childAges[childAgeIdx] : 1);
        childAgeIdx++;
      }

      // If no children, add a default 0 as in your sample
      if (childrenInRoom === 0) child_age.push(0);

      occupancy.push({
        room_no: i,
        adult: adultsInRoom,
        child: childrenInRoom,
        child_age
      });
    }

    return occupancy;
  }

    /**
   * Converts occupancy array to paxDetails array.
   * Uses the provided travelers array to populate real data; uses dummy data for remaining slots.
   * @param occupancy The occupancy array
   * @param travelers Array of traveler objects with { title: string, firstName: string, lastName: string, type?: string }
   */
  public occupancyToPaxDetails(
    occupancy: Array<any>,
    travelers: { title: string; firstName: string; lastName: string; type?: string; }[]
  ): any[] {
    const paxDetails: any[] = [];

    // Separate adults and children from travelers array based on type
    const adultTravelers = travelers.filter(t => !t.type || t.type === 'adult');
    const childTravelers = travelers.filter(t => t.type === 'child');

    let adultTravelerIndex = 0;
    let childTravelerIndex = 0;
    let adultCounter = 1;
    let childCounter = 1;

    for (let i = 0; i < occupancy.length; i++) {
      const room = occupancy[i];
      const room_no = Number(room.room_no);
      const adultCount = Number(room.adult);
      const childCount = Number(room.child);

      // Adults
      const adult: { title: string[]; firstName: string[]; lastName: string[] } = {
        title: [],
        firstName: [],
        lastName: []
      };

      for (let a = 0; a < adultCount; a++) {
        if (adultTravelerIndex < adultTravelers.length && adultTravelers[adultTravelerIndex]) {
          // Use real adult traveler data
          const traveler = adultTravelers[adultTravelerIndex];
          (adult.title as string[]).push(traveler.title || 'Mr');
          (adult.firstName as string[]).push(traveler.firstName || `test${adultCounter}`);
          (adult.lastName as string[]).push(traveler.lastName || `last${adultCounter}`);
          adultTravelerIndex++;
        } else {
          // Use dummy data if no adult traveler data available
          (adult.title as string[]).push('Mr');
          (adult.firstName as string[]).push(`test${adultCounter}`);
          (adult.lastName as string[]).push(`last${adultCounter}`);
        }
        adultCounter++;
      }

      // Children
      let child: { title: string[]; firstName: string[]; lastName: string[] } | undefined;
      if (childCount > 0) {
        child = {
          title: [],
          firstName: [],
          lastName: []
        };

        for (let c = 0; c < childCount; c++) {
          if (childTravelerIndex < childTravelers.length && childTravelers[childTravelerIndex]) {
            // Use real child traveler data
            const traveler = childTravelers[childTravelerIndex];
            (child.title as string[]).push(traveler.title || 'Master');
            (child.firstName as string[]).push(traveler.firstName || `child${childCounter}`);
            (child.lastName as string[]).push(traveler.lastName || `last${childCounter}`);
            childTravelerIndex++;
          } else {
            // Use dummy data for children if no child traveler data available
            (child.title as string[]).push('Master');
            (child.firstName as string[]).push(`child${childCounter}`);
            (child.lastName as string[]).push(`last${childCounter}`);
          }
          childCounter++;
        }
      }

      const pax: any = { room_no, adult };
      if (child) pax.child = child;
      paxDetails.push(pax);
    }
    return paxDetails;
  }

}
